import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2, Eye, EyeOff, Star, GripVertical, X, Check, Minus, LayoutGrid, Table2, ChevronUp, ChevronDown, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminSubscriptionPlans = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [previewMode, setPreviewMode] = useState(false); // Customer preview mode

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchPlans();
  }, [isAdmin, navigate]);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API}/api/admin/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlans(data);
      }
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async (plan) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/subscription-plans/${plan.plan_id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(plan)
      });

      if (res.ok) {
        toast.success('Plan updated successfully!');
        setEditingPlan(null);
        fetchPlans();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update plan');
      }
    } catch (error) {
      toast.error('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePlan = async (plan) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/subscription-plans`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(plan)
      });

      if (res.ok) {
        toast.success('Plan created successfully!');
        setShowAddPlan(false);
        fetchPlans();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create plan');
      }
    } catch (error) {
      toast.error('Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan? This cannot be undone.')) return;

    try {
      const res = await fetch(`${API}/api/admin/subscription-plans/${planId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Plan deleted');
        fetchPlans();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete plan');
      }
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const handleToggleVisibility = async (plan) => {
    const updatedPlan = { ...plan, is_visible: !plan.is_visible };
    await handleSavePlan(updatedPlan);
  };

  const handleTogglePopular = async (plan) => {
    const updatedPlan = { ...plan, is_popular: !plan.is_popular };
    await handleSavePlan(updatedPlan);
  };

  const PlanEditor = ({ plan, onSave, onCancel, isNew = false }) => {
    const [editedPlan, setEditedPlan] = useState({
      plan_id: plan?.plan_id || '',
      name: plan?.name || '',
      description: plan?.description || '',
      hive_range: plan?.hive_range || '',
      max_hives: plan?.max_hives ?? 2,
      max_crew: plan?.max_crew || plan?.max_members || 1,
      max_inspections: plan?.max_inspections || 200,
      max_apiaries: plan?.max_apiaries || 0,
      max_photos_per_inspection: plan?.max_photos_per_inspection || 5,
      max_videos_per_inspection: plan?.max_videos_per_inspection || 0,
      price_monthly: plan?.price_monthly || 0,
      price_annual: plan?.price_annual || 0,
      stripe_price_id_monthly: plan?.stripe_price_id_monthly || '',
      stripe_price_id_annual: plan?.stripe_price_id_annual || '',
      features: plan?.features || [],
      all_features: plan?.all_features ?? true,
      enabled_features: plan?.enabled_features || [],
      is_visible: plan?.is_visible ?? true,
      is_popular: plan?.is_popular || false,
      sort_order: plan?.sort_order || 99,
      button_text: plan?.button_text || 'Select Plan',
      icon: plan?.icon || ''
    });
    const [featureInput, setFeatureInput] = useState('');
    const [expandedCategories, setExpandedCategories] = useState({});

    // Comprehensive app features organized by category
    const featureCategories = [
      {
        id: 'hive_management',
        name: 'Hive Management',
        icon: '🏠',
        features: [
          { id: 'hives_basic', name: 'Add/Edit/Delete Hives', description: 'Basic hive CRUD' },
          { id: 'hives_limit_5', name: 'Hive Limit: 5', description: 'Maximum 5 hives' },
          { id: 'hives_limit_25', name: 'Hive Limit: 25', description: 'Maximum 25 hives' },
          { id: 'hives_limit_50', name: 'Hive Limit: 50', description: 'Maximum 50 hives' },
          { id: 'hives_unlimited', name: 'Unlimited Hives', description: 'No hive limit' },
          { id: 'qr_generation', name: 'QR Code Generation', description: 'Generate labels' },
          { id: 'qr_scanning', name: 'QR Code Scanning', description: 'Scan to access hive' },
          { id: 'hive_templates', name: 'Hive Templates', description: 'Save & reuse setups' },
          { id: 'copy_config', name: 'Copy Configuration', description: 'Duplicate hive settings' },
          { id: 'move_hive', name: 'Move Hive Between Apiaries', description: 'Transfer hives' },
          { id: 'archive_hives', name: 'Archive/Restore Hives', description: 'Soft delete' },
        ]
      },
      {
        id: 'apiaries',
        name: 'Apiaries/Yards',
        icon: '📍',
        features: [
          { id: 'apiaries_basic', name: 'Add/Edit/Delete Apiaries', description: 'Basic yard management' },
          { id: 'apiaries_limit_1', name: 'Apiary Limit: 1', description: 'Single apiary' },
          { id: 'apiaries_limit_5', name: 'Apiary Limit: 5', description: 'Up to 5 apiaries' },
          { id: 'apiaries_unlimited', name: 'Unlimited Apiaries', description: 'No limit' },
          { id: 'gps_capture', name: 'GPS Location Capture', description: 'Auto-capture coordinates' },
          { id: 'address_autocomplete', name: 'Address Autocomplete', description: 'Smart location lookup' },
          { id: 'weather_by_apiary', name: 'Weather by Apiary', description: 'Location-based forecasts' },
        ]
      },
      {
        id: 'team',
        name: 'Team & Crew',
        icon: '👥',
        features: [
          { id: 'crew_tasks', name: 'Assign Tasks to Crew', description: 'Delegate work' },
          { id: 'crew_inspections', name: 'Crew Can Record Inspections', description: 'Team inspections' },
          { id: 'crew_notifications', name: 'Crew Notifications', description: 'Task alerts' },
          { id: 'crew_permissions', name: 'Crew Permissions', description: 'Access control' },
          { id: 'crew_activity_log', name: 'Crew Activity Log', description: 'Track actions' },
        ]
      },
      {
        id: 'inspections',
        name: 'Inspections',
        icon: '📝',
        features: [
          { id: 'inspections_basic', name: 'Basic Inspections', description: 'Simple logging' },
          { id: 'inspections_full', name: 'Full Inspection Form', description: 'All 10+ sections' },
          { id: 'frame_by_frame', name: 'Frame-by-Frame Recording', description: 'Detailed observations' },
          { id: 'collapsible_sections', name: 'Collapsible Sections', description: 'Quick inspection mode' },
          { id: 'photo_uploads', name: 'Photo Uploads', description: 'Attach images' },
          { id: 'video_uploads', name: 'Video Uploads', description: 'Attach videos' },
          { id: 'inspection_history_30', name: 'Inspection History: 30 days', description: 'Limited history' },
          { id: 'inspection_history_1yr', name: 'Inspection History: 1 year', description: 'Extended history' },
          { id: 'inspection_history_unlimited', name: 'Unlimited History', description: 'Full access' },
          { id: 'export_pdf', name: 'Export Inspections (PDF)', description: 'Download reports' },
          { id: 'export_csv', name: 'Export Inspections (CSV)', description: 'Data export' },
        ]
      },
      {
        id: 'ai_features',
        name: 'AI Features',
        icon: '🤖',
        features: [
          { id: 'ai_assistant', name: 'AI Beekeeping Assistant', description: 'Chat for advice' },
          { id: 'ai_queries_10', name: 'AI: 10 queries/month', description: 'Limited AI usage' },
          { id: 'ai_queries_50', name: 'AI: 50 queries/month', description: 'Standard AI usage' },
          { id: 'ai_queries_unlimited', name: 'AI: Unlimited', description: 'No query limit' },
          { id: 'voice_transcription', name: 'Voice-to-Text', description: 'Whisper-powered notes' },
          { id: 'voice_5', name: 'Voice: 5/month', description: 'Limited transcriptions' },
          { id: 'voice_30', name: 'Voice: 30/month', description: 'Standard transcriptions' },
          { id: 'voice_unlimited', name: 'Voice: Unlimited', description: 'No limit' },
          { id: 'frame_scanner', name: 'AI Frame Scanner', description: 'Analyze brood frames' },
          { id: 'scans_5', name: 'Frame Scans: 5/month', description: 'Limited scans' },
          { id: 'scans_20', name: 'Frame Scans: 20/month', description: 'Standard scans' },
          { id: 'scans_unlimited', name: 'Frame Scans: Unlimited', description: 'No scan limit' },
          { id: 'ai_progress_reports', name: 'AI Progress Reports', description: 'Hive summaries' },
          { id: 'ai_all_hives_report', name: 'AI All-Hives Report', description: 'Full apiary analysis' },
        ]
      },
      {
        id: 'mite_control',
        name: 'Mite Control',
        icon: '🐛',
        features: [
          { id: 'mite_counts', name: 'Record Mite Counts', description: 'Log mite levels' },
          { id: 'mite_history', name: 'Mite Count History', description: 'View past counts' },
          { id: 'treatment_wizard', name: 'Treatment Plan Wizard', description: 'Guided setup' },
          { id: 'treatment_tracking', name: 'Active Treatment Tracking', description: 'Monitor treatments' },
          { id: 'treatment_schedule', name: 'Treatment Schedule', description: 'Auto task lists' },
          { id: 'treatments_preconfigured', name: '16 Pre-configured Treatments', description: 'Organic, chemical, etc.' },
          { id: 'treatments_custom', name: 'Custom Treatment Types', description: 'Add your own' },
        ]
      },
      {
        id: 'splits',
        name: 'Splits',
        icon: '✂️',
        features: [
          { id: 'split_wizard', name: 'Split Wizard', description: 'Guided split process' },
          { id: 'split_tracking', name: 'Track Split History', description: 'View all splits' },
          { id: 'split_from_parent', name: 'Link to Parent Hive', description: 'Track lineage' },
          { id: 'split_types', name: 'Multiple Split Types', description: 'Walk-away, Nucleus, etc.' },
          { id: 'split_notes', name: 'Split Notes & Documentation', description: 'Record details' },
          { id: 'split_timeline', name: 'Split Timeline View', description: 'Visual history' },
        ]
      },
      {
        id: 'queen_management',
        name: 'Queen Management',
        icon: '👑',
        features: [
          { id: 'queen_tracking', name: 'Track Queen Details', description: 'Queen info & status' },
          { id: 'queen_marking', name: 'Queen Marking Colors', description: 'Year-based colors' },
          { id: 'queen_age', name: 'Queen Age Tracking', description: 'Monitor queen age' },
          { id: 'queen_lineage', name: 'Queen Lineage/Genetics', description: 'Track genetics' },
          { id: 'queen_rearing', name: 'Queen Rearing Calendar', description: 'Rearing schedule' },
          { id: 'queen_cells', name: 'Queen Cell Tracking', description: 'Monitor queen cells' },
          { id: 'requeening_alerts', name: 'Requeening Reminders', description: 'When to requeen' },
          { id: 'queen_performance', name: 'Queen Performance Notes', description: 'Rate performance' },
        ]
      },
      {
        id: 'swarm_management',
        name: 'Swarm Management',
        icon: '🐝',
        features: [
          { id: 'swarm_alerts', name: 'Swarm Risk Alerts', description: 'Early warning signs' },
          { id: 'swarm_prevention', name: 'Swarm Prevention Checklist', description: 'Prevention steps' },
          { id: 'swarm_capture', name: 'Log Swarm Captures', description: 'Record captures' },
          { id: 'swarm_traps', name: 'Swarm Trap Locations', description: 'Manage trap sites' },
          { id: 'swarm_calendar', name: 'Swarm Season Calendar', description: 'Peak swarm times' },
          { id: 'swarm_ready_hives', name: 'Ready Hives for Swarms', description: 'Prepared equipment' },
        ]
      },
      {
        id: 'harvest',
        name: 'Harvest & Products',
        icon: '🍯',
        features: [
          { id: 'harvest_logging', name: 'Log Harvests', description: 'Record honey harvests' },
          { id: 'harvest_summary', name: 'Harvest Summary Cards', description: 'Season & all-time stats' },
          { id: 'harvest_charts', name: 'Harvest Charts', description: 'Visual yearly breakdown' },
          { id: 'harvest_history', name: 'Harvest History', description: 'View past harvests' },
          { id: 'custom_honey_types', name: 'Custom Honey Types', description: 'Admin-defined types' },
          { id: 'quality_grades', name: 'Quality Grades', description: 'Grade your harvests' },
          { id: 'export_harvest', name: 'Export Harvest Data', description: 'Download records' },
          { id: 'products', name: 'Products Catalogue', description: 'Create and manage products from harvests' },
          { id: 'record_sales', name: 'Record Sales', description: 'Track product sales and revenue' },
        ]
      },
      {
        id: 'calendar',
        name: 'Calendar & Reminders',
        icon: '📅',
        features: [
          { id: 'calendar_view', name: 'Monthly Calendar View', description: 'See tasks on calendar' },
          { id: 'reminders', name: 'Reminders/Tasks', description: 'Dashboard reminders' },
          { id: 'planned_actions', name: 'Planned Actions', description: 'Schedule future tasks' },
          { id: 'queen_rearing_reminders', name: 'Queen Rearing Reminders', description: 'Milestone alerts' },
          { id: 'email_reminders', name: 'Email Reminders', description: 'Email notifications' },
          { id: 'calendar_filtering', name: 'Apiary-Filtered Calendar', description: 'Filter by yard' },
        ]
      },
      {
        id: 'dashboard',
        name: 'Dashboard',
        icon: '📊',
        features: [
          { id: 'dashboard_basic', name: 'Basic Dashboard', description: 'Simple overview' },
          { id: 'dashboard_full', name: 'Full Dashboard', description: 'All stat cards' },
          { id: 'attention_center', name: 'Attention Center', description: 'Alerts & overdue' },
          { id: 'quick_action_bar', name: 'Quick Action Bar', description: 'One-click actions' },
          { id: 'header_search', name: 'Header Search', description: 'Find hives instantly' },
          { id: 'apiary_selector', name: 'Apiary Selector', description: 'Filter by yard' },
        ]
      },
      {
        id: 'knowledge',
        name: 'Knowledge Library',
        icon: '📚',
        features: [
          { id: 'resources', name: 'Resources (Videos, Books)', description: 'Curated content' },
          { id: 'articles', name: 'Articles', description: 'Knowledge base' },
          { id: 'glossary', name: 'Glossary', description: 'Beekeeping terms' },
          { id: 'ai_knowledge', name: 'AI Assistant in Knowledge', description: 'Ask questions' },
        ]
      },
      {
        id: 'community',
        name: 'Community & DIY',
        icon: '🐝',
        features: [
          { id: 'hive_mind_view', name: 'View Hive Mind Map', description: 'See activity' },
          { id: 'hive_mind_post', name: 'Post to Hive Mind', description: 'Share activities' },
          { id: 'activity_filters', name: 'Activity Filters', description: 'Filter by type' },
          { id: 'beekeeper_map', name: 'Beekeeper Map', description: 'Find beekeepers' },
          { id: 'radius_search', name: 'Radius Search', description: 'Search by distance' },
          { id: 'diy_ideas_view', name: 'Browse DIY Ideas', description: 'View community ideas and projects' },
          { id: 'diy_ideas_share', name: 'Share DIY Ideas', description: 'Post your own ideas and projects' },
          { id: 'diy_challenges', name: 'DIY Challenges', description: 'Participate in monthly challenges' },
        ]
      },
      {
        id: 'admin',
        name: 'Admin & Customization',
        icon: '⚙️',
        features: [
          { id: 'custom_tasks', name: 'Custom Task Types', description: 'Add your own tasks' },
          { id: 'custom_actions', name: 'Custom Action Types', description: 'Add your own actions' },
          { id: 'suggest_tasks', name: 'Suggest Tasks/Actions', description: 'User suggestions' },
          { id: 'config_hive_types', name: 'Configure Hive Types', description: 'Customize options' },
          { id: 'config_bee_varieties', name: 'Configure Bee Varieties', description: 'Customize bees' },
          { id: 'config_tools', name: 'Configure Tools', description: 'Customize tools' },
          { id: 'config_alerts', name: 'Configure Critical Alerts', description: 'Alert triggers' },
          { id: 'config_harvest', name: 'Configure Harvest Settings', description: 'Honey types' },
          { id: 'shuffle_resources', name: 'Shuffle Resource Order', description: 'Fair visibility' },
        ]
      },
      {
        id: 'reports',
        name: 'Reports & Analytics',
        icon: '📈',
        features: [
          { id: 'analytics_basic', name: 'Basic Analytics', description: 'Simple stats' },
          { id: 'analytics_advanced', name: 'Advanced Analytics', description: 'Detailed insights' },
          { id: 'health_trends', name: 'Hive Health Trends', description: 'Over-time analysis' },
          { id: 'harvest_analytics', name: 'Harvest Analytics', description: 'Yield tracking' },
          { id: 'mite_trends', name: 'Mite Level Trends', description: 'Infestation tracking' },
          { id: 'export_all', name: 'Export All Data', description: 'Full download' },
        ]
      },
      {
        id: 'support',
        name: 'Support & Access',
        icon: '🎧',
        features: [
          { id: 'support_email', name: 'Email Support', description: 'Standard support' },
          { id: 'support_priority', name: 'Priority Support', description: 'Faster response' },
          { id: 'support_phone', name: 'Phone Support', description: 'Direct assistance' },
          { id: 'training', name: 'Training Sessions', description: '1-on-1 onboarding' },
          { id: 'early_access', name: 'Early Access to Features', description: 'Beta testing' },
          { id: 'feature_requests', name: 'Feature Requests Priority', description: 'Influence roadmap' },
        ]
      },
    ];

    const toggleCategory = (categoryId) => {
      setExpandedCategories(prev => ({
        ...prev,
        [categoryId]: !prev[categoryId]
      }));
    };

    const toggleFeature = (featureId) => {
      setEditedPlan(prev => {
        const current = prev.enabled_features || [];
        if (current.includes(featureId)) {
          return { ...prev, enabled_features: current.filter(f => f !== featureId) };
        } else {
          return { ...prev, enabled_features: [...current, featureId] };
        }
      });
    };

    const selectAllInCategory = (categoryId) => {
      const category = featureCategories.find(c => c.id === categoryId);
      if (!category) return;
      const featureIds = category.features.map(f => f.id);
      setEditedPlan(prev => ({
        ...prev,
        enabled_features: [...new Set([...(prev.enabled_features || []), ...featureIds])]
      }));
    };

    const deselectAllInCategory = (categoryId) => {
      const category = featureCategories.find(c => c.id === categoryId);
      if (!category) return;
      const featureIds = category.features.map(f => f.id);
      setEditedPlan(prev => ({
        ...prev,
        enabled_features: (prev.enabled_features || []).filter(f => !featureIds.includes(f))
      }));
    };

    const getCategorySelectedCount = (categoryId) => {
      const category = featureCategories.find(c => c.id === categoryId);
      if (!category) return 0;
      return category.features.filter(f => editedPlan.enabled_features?.includes(f.id)).length;
    };

    const addFeature = () => {
      if (featureInput.trim()) {
        setEditedPlan(prev => ({
          ...prev,
          features: [...prev.features, featureInput.trim()]
        }));
        setFeatureInput('');
      }
    };

    const removeFeature = (index) => {
      setEditedPlan(prev => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index)
      }));
    };

    const moveFeature = (index, direction) => {
      setEditedPlan(prev => {
        const features = [...prev.features];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= features.length) return prev;
        [features[index], features[newIndex]] = [features[newIndex], features[index]];
        return { ...prev, features };
      });
    };

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-stone-200 flex justify-between items-center sticky top-0 bg-white">
            <h2 className="text-xl font-bold text-stone-800">
              {isNew ? '➕ Create New Plan' : `✏️ Edit: ${plan.name}`}
            </h2>
            <button onClick={onCancel} className="p-2 hover:bg-stone-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan ID (unique, lowercase)</Label>
                <Input
                  value={editedPlan.plan_id}
                  onChange={e => setEditedPlan(prev => ({ ...prev, plan_id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="e.g., hobby, club"
                  disabled={!isNew}
                  className={!isNew ? 'bg-stone-100' : ''}
                />
              </div>
              <div>
                <Label>Display Name</Label>
                <Input
                  value={editedPlan.name}
                  onChange={e => setEditedPlan(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Hobby, Club"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={editedPlan.description}
                onChange={e => setEditedPlan(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Perfect for backyard beekeepers"
              />
            </div>

            {/* Plan Icon */}
            <div>
              <Label>Plan Icon</Label>
              <div className="flex items-center gap-4 mt-2">
                {editedPlan.icon ? (
                  <div className="relative w-16 h-16 rounded-xl border-2 border-amber-300 overflow-hidden bg-white flex items-center justify-center">
                    <img
                      src={editedPlan.icon.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL || ''}${editedPlan.icon}` : editedPlan.icon}
                      alt="Plan icon"
                      className="w-12 h-12 object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => setEditedPlan(prev => ({ ...prev, icon: '' }))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 flex items-center justify-center text-stone-400 text-xs">
                    No icon
                  </div>
                )}
                <div className="flex-1">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg text-sm font-medium transition-colors">
                    <Plus className="w-4 h-4" />
                    Upload Icon
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${API}/api/upload`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` },
                            body: formData
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setEditedPlan(prev => ({ ...prev, icon: data.url }));
                            toast.success('Icon uploaded');
                          } else {
                            toast.error('Upload failed');
                          }
                        } catch { toast.error('Upload failed'); }
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <p className="text-xs text-stone-400 mt-1">Square image recommended (e.g. 128x128px)</p>
                </div>
              </div>
            </div>

            {/* Limits */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-3">📊 Plan Limits</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-blue-700">Hive Range (display)</Label>
                  <Input
                    value={editedPlan.hive_range}
                    onChange={e => setEditedPlan(prev => ({ ...prev, hive_range: e.target.value }))}
                    placeholder="1-20 hives"
                  />
                </div>
                <div>
                  <Label className="text-blue-700">Max Hives</Label>
                  <Input
                    type="number"
                    value={editedPlan.max_hives}
                    onChange={e => setEditedPlan(prev => ({ ...prev, max_hives: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label className="text-blue-700">Max Apiaries</Label>
                  <Input
                    type="number"
                    value={editedPlan.max_apiaries}
                    onChange={e => setEditedPlan(prev => ({ ...prev, max_apiaries: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-blue-600 mt-1">0 = unlimited</p>
                </div>
                <div>
                  <Label className="text-blue-700">Max Crew</Label>
                  <Input
                    type="number"
                    value={editedPlan.max_crew}
                    onChange={e => setEditedPlan(prev => ({ ...prev, max_crew: parseInt(e.target.value) || 1 }))}
                  />
                  <p className="text-xs text-blue-600 mt-1">Beekeepers per account</p>
                </div>
                <div>
                  <Label className="text-blue-700">Max Inspections</Label>
                  <Input
                    type="number"
                    value={editedPlan.max_inspections}
                    onChange={e => setEditedPlan(prev => ({ ...prev, max_inspections: parseInt(e.target.value) || 200 }))}
                  />
                  <p className="text-xs text-blue-600 mt-1">Per month</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <Label className="text-blue-700">Photos per Inspection</Label>
                  <Input
                    type="number"
                    value={editedPlan.max_photos_per_inspection}
                    onChange={e => setEditedPlan(prev => ({ ...prev, max_photos_per_inspection: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-blue-600 mt-1">Per hive inspection</p>
                </div>
                <div>
                  <Label className="text-blue-700">Videos per Inspection</Label>
                  <Input
                    type="number"
                    value={editedPlan.max_videos_per_inspection}
                    onChange={e => setEditedPlan(prev => ({ ...prev, max_videos_per_inspection: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-blue-600 mt-1">0 = no video upload</p>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editedPlan.price_monthly}
                  onChange={e => setEditedPlan(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Annual Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editedPlan.price_annual}
                  onChange={e => setEditedPlan(prev => ({ ...prev, price_annual: parseFloat(e.target.value) || 0 }))}
                />
                {editedPlan.price_monthly > 0 && (
                  <p className="text-xs text-stone-500 mt-1">
                    Suggested: ${(editedPlan.price_monthly * 10).toFixed(0)} (2 months free)
                  </p>
                )}
              </div>
            </div>

            {/* Stripe Price IDs */}
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                💳 Stripe Integration
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-purple-700">Monthly Price ID</Label>
                  <Input
                    value={editedPlan.stripe_price_id_monthly}
                    onChange={e => setEditedPlan(prev => ({ ...prev, stripe_price_id_monthly: e.target.value }))}
                    placeholder="price_1abc..."
                    className="font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-purple-700">Annual Price ID</Label>
                  <Input
                    value={editedPlan.stripe_price_id_annual}
                    onChange={e => setEditedPlan(prev => ({ ...prev, stripe_price_id_annual: e.target.value }))}
                    placeholder="price_1xyz..."
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                Get Price IDs from Stripe Dashboard → Products → Click product → Copy Price ID
              </p>
            </div>

            {/* App Feature Access Control */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-green-800">🔐 Feature Access Control</h4>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editedPlan.all_features}
                    onChange={e => setEditedPlan(prev => ({ ...prev, all_features: e.target.checked }))}
                    className="rounded text-green-600"
                  />
                  <span className="font-medium text-green-700">All Features</span>
                </label>
              </div>
              
              {!editedPlan.all_features && (
                <div className="space-y-2 mt-3 max-h-[400px] overflow-y-auto pr-2">
                  {featureCategories.map(category => {
                    const selectedCount = getCategorySelectedCount(category.id);
                    const totalCount = category.features.length;
                    const isExpanded = expandedCategories[category.id];
                    
                    return (
                      <div key={category.id} className="border border-stone-200 rounded-lg bg-white overflow-hidden">
                        {/* Category Header */}
                        <div 
                          className="flex items-center justify-between p-2 bg-stone-50 cursor-pointer hover:bg-stone-100"
                          onClick={() => toggleCategory(category.id)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{category.icon}</span>
                            <span className="font-medium text-sm text-stone-800">{category.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              selectedCount === totalCount 
                                ? 'bg-green-100 text-green-700' 
                                : selectedCount > 0 
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-stone-100 text-stone-500'
                            }`}>
                              {selectedCount}/{totalCount}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); selectAllInCategory(category.id); }}
                              className="text-xs px-2 py-1 text-green-600 hover:bg-green-50 rounded"
                            >
                              All
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); deselectAllInCategory(category.id); }}
                              className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              None
                            </button>
                            <span className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                          </div>
                        </div>
                        
                        {/* Category Features */}
                        {isExpanded && (
                          <div className="p-2 grid grid-cols-1 md:grid-cols-2 gap-1">
                            {category.features.map(feature => (
                              <label
                                key={feature.id}
                                className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                                  editedPlan.enabled_features?.includes(feature.id)
                                    ? 'bg-green-50 border border-green-300'
                                    : 'hover:bg-stone-50 border border-transparent'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={editedPlan.enabled_features?.includes(feature.id) || false}
                                  onChange={() => toggleFeature(feature.id)}
                                  className="rounded text-green-600 mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium text-stone-700 block">{feature.name}</span>
                                  <span className="text-xs text-stone-500">{feature.description}</span>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {editedPlan.all_features && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ Users on this plan have access to all app features
                </p>
              )}
              
              {!editedPlan.all_features && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <p className="text-sm text-stone-600">
                    <strong>{editedPlan.enabled_features?.length || 0}</strong> features selected
                  </p>
                  {editedPlan.enabled_features?.length === 0 && (
                    <p className="text-sm text-orange-600 mt-1">
                      ⚠️ No features selected - users will have very limited access
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Display Features List (for pricing page) */}
            <div>
              <Label>Display Features (shown on pricing page)</Label>
              <div className="space-y-2 mt-2">
                {editedPlan.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 bg-stone-50 p-2 rounded-lg">
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => moveFeature(index, -1)}
                        disabled={index === 0}
                        className="p-0.5 hover:bg-stone-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="w-3.5 h-3.5 text-stone-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveFeature(index, 1)}
                        disabled={index === editedPlan.features.length - 1}
                        className="p-0.5 hover:bg-stone-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="w-3.5 h-3.5 text-stone-600" />
                      </button>
                    </div>
                    <span className="text-xs text-stone-400 w-5 text-center">{index + 1}</span>
                    <span className="flex-1 text-sm">{feature}</span>
                    <button
                      onClick={() => removeFeature(index)}
                      className="p-1 hover:bg-red-100 rounded text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={featureInput}
                  onChange={e => setFeatureInput(e.target.value)}
                  placeholder="Add a feature..."
                  onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button type="button" variant="outline" onClick={addFeature}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Display Options */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input
                  value={editedPlan.button_text}
                  onChange={e => setEditedPlan(prev => ({ ...prev, button_text: e.target.value }))}
                  placeholder="Select Plan"
                />
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input
                  type="number"
                  value={editedPlan.sort_order}
                  onChange={e => setEditedPlan(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editedPlan.is_visible}
                      onChange={e => setEditedPlan(prev => ({ ...prev, is_visible: e.target.checked }))}
                      className="rounded"
                    />
                    Visible
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editedPlan.is_popular}
                      onChange={e => setEditedPlan(prev => ({ ...prev, is_popular: e.target.checked }))}
                      className="rounded"
                    />
                    Popular
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-stone-200 flex justify-end gap-3 sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button 
              onClick={() => onSave(editedPlan)} 
              disabled={saving || !editedPlan.plan_id || !editedPlan.name}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : (isNew ? 'Create Plan' : 'Save Changes')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Feature categories for the comparison table (same structure used in PlanEditor)
  const featureCategories = [
    {
      id: 'hive_management',
      name: 'Hive Management',
      icon: '🏠',
      features: [
        { id: 'hives_basic', name: 'Add/Edit/Delete Hives' },
        { id: 'max_hives', name: 'Maximum Hives', type: 'dynamic', field: 'max_hives' },
        { id: 'qr_generation', name: 'QR Code Generation' },
        { id: 'qr_scanning', name: 'QR Code Scanning' },
        { id: 'hive_templates', name: 'Hive Templates' },
        { id: 'copy_config', name: 'Copy Configuration' },
        { id: 'move_hive', name: 'Move Hive Between Apiaries' },
        { id: 'archive_hives', name: 'Archive/Restore Hives' },
      ]
    },
    {
      id: 'apiaries',
      name: 'Apiaries/Yards',
      icon: '📍',
      features: [
        { id: 'apiaries_basic', name: 'Add/Edit/Delete Apiaries' },
        { id: 'max_apiaries', name: 'Maximum Apiaries', type: 'dynamic', field: 'max_apiaries', default: 'Unlimited' },
        { id: 'gps_capture', name: 'GPS Location Capture' },
        { id: 'address_autocomplete', name: 'Address Autocomplete' },
        { id: 'weather_by_apiary', name: 'Weather by Apiary' },
      ]
    },
    {
      id: 'team',
      name: 'Team & Crew',
      icon: '👥',
      features: [
        { id: 'max_crew', name: 'Maximum Crew Members', type: 'dynamic', field: 'max_crew', fallbackField: 'max_members' },
        { id: 'crew_tasks', name: 'Assign Tasks to Crew' },
        { id: 'crew_inspections', name: 'Crew Can Record Inspections' },
        { id: 'crew_notifications', name: 'Crew Notifications' },
      ]
    },
    {
      id: 'inspections',
      name: 'Inspections',
      icon: '📝',
      features: [
        { id: 'inspections_basic', name: 'Basic Inspections' },
        { id: 'inspections_full', name: 'Full Inspection Form' },
        { id: 'max_inspections', name: 'Inspections per Month', type: 'dynamic', field: 'max_inspections', suffix: '/mo' },
        { id: 'frame_by_frame', name: 'Frame-by-Frame Recording' },
        { id: 'collapsible_sections', name: 'Collapsible Sections' },
        { id: 'photo_uploads', name: 'Photo Uploads' },
        { id: 'video_uploads', name: 'Video Uploads' },
        { id: 'inspection_history_unlimited', name: 'Unlimited History' },
        { id: 'export_pdf', name: 'Export Inspections (PDF)' },
        { id: 'export_csv', name: 'Export Inspections (CSV)' },
      ]
    },
    {
      id: 'ai_features',
      name: 'AI Features',
      icon: '🤖',
      features: [
        { id: 'ai_assistant', name: 'AI Beekeeping Assistant' },
        { id: 'ai_queries_10', name: 'AI: 10 queries/month' },
        { id: 'ai_queries_50', name: 'AI: 50 queries/month' },
        { id: 'ai_queries_unlimited', name: 'AI: Unlimited' },
        { id: 'voice_transcription', name: 'Voice-to-Text' },
        { id: 'voice_5', name: 'Voice: 5/month' },
        { id: 'voice_30', name: 'Voice: 30/month' },
        { id: 'voice_unlimited', name: 'Voice: Unlimited' },
        { id: 'frame_scanner', name: 'AI Frame Scanner' },
        { id: 'scans_5', name: 'Frame Scans: 5/month' },
        { id: 'scans_20', name: 'Frame Scans: 20/month' },
        { id: 'scans_unlimited', name: 'Frame Scans: Unlimited' },
        { id: 'ai_progress_reports', name: 'AI Progress Reports' },
        { id: 'ai_all_hives_report', name: 'AI All-Hives Report' },
      ]
    },
    {
      id: 'mite_control',
      name: 'Mite Control',
      icon: '🐛',
      features: [
        { id: 'mite_counts', name: 'Record Mite Counts' },
        { id: 'mite_history', name: 'Mite Count History' },
        { id: 'treatment_wizard', name: 'Treatment Plan Wizard' },
        { id: 'treatment_tracking', name: 'Active Treatment Tracking' },
        { id: 'treatment_schedule', name: 'Treatment Schedule' },
        { id: 'treatments_preconfigured', name: '16 Pre-configured Treatments' },
        { id: 'treatments_custom', name: 'Custom Treatment Types' },
      ]
    },
    {
      id: 'splits',
      name: 'Splits',
      icon: '✂️',
      features: [
        { id: 'split_wizard', name: 'Split Wizard' },
        { id: 'split_tracking', name: 'Track Split History' },
        { id: 'split_from_parent', name: 'Link to Parent Hive' },
        { id: 'split_types', name: 'Multiple Split Types (Walk-away, Nucleus, etc.)' },
        { id: 'split_notes', name: 'Split Notes & Documentation' },
        { id: 'split_timeline', name: 'Split Timeline View' },
      ]
    },
    {
      id: 'queen_management',
      name: 'Queen Management',
      icon: '👑',
      features: [
        { id: 'queen_tracking', name: 'Track Queen Details' },
        { id: 'queen_marking', name: 'Queen Marking Colors' },
        { id: 'queen_age', name: 'Queen Age Tracking' },
        { id: 'queen_lineage', name: 'Queen Lineage/Genetics' },
        { id: 'queen_rearing', name: 'Queen Rearing Calendar' },
        { id: 'queen_cells', name: 'Queen Cell Tracking' },
        { id: 'requeening_alerts', name: 'Requeening Reminders' },
        { id: 'queen_performance', name: 'Queen Performance Notes' },
      ]
    },
    {
      id: 'swarm_management',
      name: 'Swarm Management',
      icon: '🐝',
      features: [
        { id: 'swarm_alerts', name: 'Swarm Risk Alerts' },
        { id: 'swarm_prevention', name: 'Swarm Prevention Checklist' },
        { id: 'swarm_capture', name: 'Log Swarm Captures' },
        { id: 'swarm_traps', name: 'Swarm Trap Locations' },
        { id: 'swarm_calendar', name: 'Swarm Season Calendar' },
        { id: 'swarm_ready_hives', name: 'Ready Hives for Swarms' },
      ]
    },
    {
      id: 'harvest',
      name: 'Harvest Tracking',
      icon: '🍯',
      features: [
        { id: 'harvest_logging', name: 'Log Harvests' },
        { id: 'harvest_summary', name: 'Harvest Summary Cards' },
        { id: 'harvest_charts', name: 'Harvest Charts' },
        { id: 'harvest_history', name: 'Harvest History' },
        { id: 'custom_honey_types', name: 'Custom Honey Types' },
        { id: 'quality_grades', name: 'Quality Grades' },
        { id: 'export_harvest', name: 'Export Harvest Data' },
      ]
    },
    {
      id: 'calendar',
      name: 'Calendar & Reminders',
      icon: '📅',
      features: [
        { id: 'calendar_view', name: 'Monthly Calendar View' },
        { id: 'reminders', name: 'Reminders/Tasks' },
        { id: 'planned_actions', name: 'Planned Actions' },
        { id: 'queen_rearing_reminders', name: 'Queen Rearing Reminders' },
        { id: 'email_reminders', name: 'Email Reminders' },
        { id: 'calendar_filtering', name: 'Apiary-Filtered Calendar' },
      ]
    },
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: '📊',
      features: [
        { id: 'dashboard_basic', name: 'Basic Dashboard' },
        { id: 'dashboard_full', name: 'Full Dashboard' },
        { id: 'attention_center', name: 'Attention Center' },
        { id: 'quick_action_bar', name: 'Quick Action Bar' },
        { id: 'header_search', name: 'Header Search' },
        { id: 'apiary_selector', name: 'Apiary Selector' },
      ]
    },
    {
      id: 'support',
      name: 'Support & Access',
      icon: '🎧',
      features: [
        { id: 'support_email', name: 'Email Support' },
        { id: 'support_priority', name: 'Priority Support' },
        { id: 'support_phone', name: 'Phone Support' },
        { id: 'training', name: 'Training Sessions' },
        { id: 'early_access', name: 'Early Access to Features' },
        { id: 'feature_requests', name: 'Feature Requests Priority' },
      ]
    },
  ];

  // Check if a plan has a specific feature enabled
  const planHasFeature = (plan, featureId) => {
    if (plan.all_features) return true;
    return plan.enabled_features?.includes(featureId) || false;
  };

  // Get dynamic value from plan (for limits like max_hives, max_crew, etc.)
  const getDynamicValue = (plan, feature) => {
    const value = plan[feature.field] || (feature.fallbackField ? plan[feature.fallbackField] : null);
    if (value === null || value === undefined) {
      return feature.default || '—';
    }
    if (value >= 9999 || value === -1) {
      return 'Unlimited';
    }
    return `${value}${feature.suffix || ''}`;
  };

  // Sort plans by sort_order
  const sortedPlans = [...plans].sort((a, b) => (a.sort_order || 99) - (b.sort_order || 99));

  // Filter plans for display (in preview mode, only show visible plans)
  const displayPlans = previewMode 
    ? sortedPlans.filter(p => p.is_visible)
    : sortedPlans.filter(p => p.is_visible);

  // Feature Comparison Table Component
  const FeatureComparisonTable = () => (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border ${previewMode ? 'border-blue-300 ring-2 ring-blue-100' : 'border-stone-200'}`}>
      {/* Preview Mode Banner */}
      {previewMode && (
        <div className="bg-blue-500 text-white text-center py-2 px-4 text-sm font-medium">
          👁️ Customer Preview Mode — This is how customers see your pricing table
        </div>
      )}
      
      {/* Sticky Header with Plan Names */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-gradient-to-r from-amber-100 to-orange-100">
              <th className="text-left p-4 font-semibold text-stone-800 min-w-[250px] sticky left-0 bg-gradient-to-r from-amber-100 to-amber-50 z-10">
                Feature
              </th>
              {displayPlans.map(plan => (
                <th key={plan.plan_id} className="text-center p-4 min-w-[140px]">
                  <div className="flex flex-col items-center gap-1">
                    {plan.is_popular && (
                      <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Star className="w-2.5 h-2.5" /> POPULAR
                      </span>
                    )}
                    <span className="font-bold text-stone-800">{plan.name}</span>
                    <span className="text-sm font-normal text-stone-500">
                      {plan.price_monthly === 0 ? 'Free' : `$${plan.price_monthly}/mo`}
                    </span>
                    {/* Show "Select Plan" button in preview mode */}
                    {previewMode && (
                      <button className={`mt-2 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        plan.is_popular 
                          ? 'bg-amber-500 text-white hover:bg-amber-600' 
                          : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                      }`}>
                        {plan.button_text || 'Select Plan'}
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureCategories.map((category, catIndex) => (
              <React.Fragment key={category.id}>
                {/* Category Header Row */}
                <tr className="bg-stone-100">
                  <td 
                    colSpan={displayPlans.length + 1} 
                    className="p-3 font-semibold text-stone-700 sticky left-0 bg-stone-100 z-10"
                  >
                    <span className="mr-2">{category.icon}</span>
                    {category.name}
                  </td>
                </tr>
                {/* Feature Rows */}
                {category.features.map((feature, featureIndex) => (
                  <tr 
                    key={feature.id} 
                    className={`border-b border-stone-100 hover:bg-amber-50/50 transition-colors ${
                      featureIndex % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                    }`}
                  >
                    <td className="p-3 text-sm text-stone-700 sticky left-0 bg-inherit z-10 border-r border-stone-100">
                      {feature.name}
                    </td>
                    {displayPlans.map(plan => (
                      <td key={`${plan.plan_id}-${feature.id}`} className="p-3 text-center">
                        {feature.type === 'dynamic' ? (
                          // Dynamic value (like max_hives, max_crew)
                          <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-semibold ${
                            getDynamicValue(plan, feature) === 'Unlimited' 
                              ? 'bg-green-100 text-green-700'
                              : getDynamicValue(plan, feature) === '—'
                                ? 'bg-stone-100 text-stone-400'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {getDynamicValue(plan, feature)}
                          </span>
                        ) : planHasFeature(plan, feature.id) ? (
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100">
                            <Check className="w-4 h-4 text-green-600" strokeWidth={3} />
                          </div>
                        ) : (
                          <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-stone-100">
                            <Minus className="w-4 h-4 text-stone-400" />
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Footer */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 border-t border-stone-200">
        <div className="flex items-center justify-between">
          <p className="text-sm text-stone-600">
            <strong>{featureCategories.reduce((sum, cat) => sum + cat.features.length, 0)}</strong> features across <strong>{featureCategories.length}</strong> categories
          </p>
          {previewMode ? (
            <p className="text-sm text-blue-600 font-medium">
              ✨ Tip: Click "Exit Preview" to return to admin view
            </p>
          ) : (
            <p className="text-sm text-stone-500">
              Plans with "All Features" have access to everything
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-amber-50 to-orange-50 flex items-center justify-center">
        <p className="text-stone-600">Loading plans...</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-amber-50 to-orange-50 p-4">
      {/* Header */}
      <div className="max-w-7xl mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <CreditCard className="h-6 w-6 text-amber-600" />
              Subscription Plans
            </h1>
            <p className="text-sm text-stone-500 mt-1">Manage pricing, features, and visibility</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* View Toggle */}
            <div className="flex items-center bg-white rounded-lg border border-stone-200 p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-amber-500 text-white' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <Table2 className="w-4 h-4" /> Comparison
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'cards' 
                    ? 'bg-amber-500 text-white' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
              >
                <LayoutGrid className="w-4 h-4" /> Cards
              </button>
            </div>
            {/* Preview as Customer Button */}
            {viewMode === 'table' && (
              <Button 
                variant={previewMode ? "default" : "outline"}
                onClick={() => setPreviewMode(!previewMode)}
                className={previewMode ? "bg-blue-500 hover:bg-blue-600" : ""}
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewMode ? 'Exit Preview' : 'Preview as Customer'}
              </Button>
            )}
            <Button onClick={() => setShowAddPlan(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" /> Add Plan
            </Button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl">
        {viewMode === 'table' ? (
          <FeatureComparisonTable />
        ) : (
          /* Plans Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map(plan => (
            <Card key={plan.plan_id} className={`relative ${!plan.is_visible ? 'opacity-60' : ''}`}>
              {/* Badges */}
              <div className="absolute top-3 right-3 flex gap-2">
                {plan.is_popular && (
                  <span className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3" /> POPULAR
                  </span>
                )}
                {!plan.is_visible && (
                  <span className="bg-stone-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    HIDDEN FROM USERS
                  </span>
                )}
                {plan.plan_id === 'free' && (
                  <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    🎁 SPECIAL GIFT PLAN
                  </span>
                )}
              </div>

              <CardHeader>
                <div className="flex items-center gap-3">
                  {plan.icon && (
                    <img
                      src={plan.icon.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL || ''}${plan.icon}` : plan.icon}
                      alt=""
                      className="w-10 h-10 object-contain rounded-lg"
                    />
                  )}
                  <div>
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <p className="text-sm text-stone-500">{plan.description}</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {/* Pricing */}
                <div className="mb-4 p-3 bg-stone-50 rounded-lg">
                  <div className="flex justify-between items-baseline">
                    <span className="text-2xl font-bold text-stone-800">${plan.price_monthly}</span>
                    <span className="text-stone-500">/month</span>
                  </div>
                  <p className="text-sm text-stone-500">${plan.price_annual}/year</p>
                </div>

                {/* Limits */}
                <div className="grid grid-cols-4 gap-2 mb-4 text-sm">
                  <div className="bg-blue-50 p-2 rounded text-center">
                    <p className="font-bold text-blue-700">{plan.max_hives}</p>
                    <p className="text-xs text-blue-600">Hives</p>
                  </div>
                  <div className="bg-teal-50 p-2 rounded text-center">
                    <p className="font-bold text-teal-700">{plan.max_apiaries || '∞'}</p>
                    <p className="text-xs text-teal-600">Apiaries</p>
                  </div>
                  <div className="bg-purple-50 p-2 rounded text-center">
                    <p className="font-bold text-purple-700">{plan.max_crew || plan.max_members || 1}</p>
                    <p className="text-xs text-purple-600">Crew</p>
                  </div>
                  <div className="bg-amber-50 p-2 rounded text-center">
                    <p className="font-bold text-amber-700">{plan.max_inspections || '∞'}</p>
                    <p className="text-xs text-amber-600">Inspects</p>
                  </div>
                </div>

                {/* Feature Access */}
                <div className="mb-4 p-2 bg-green-50 rounded-lg">
                  <p className="text-xs font-medium text-green-700 mb-1">🔐 Features</p>
                  <span className={`text-xs px-2 py-0.5 rounded ${plan.all_features ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                    {plan.all_features ? '✓ All Features' : `${plan.enabled_features?.length || 0} features enabled`}
                  </span>
                </div>

                {/* Features Preview */}
                <div className="mb-4">
                  <p className="text-xs text-stone-500 mb-1">{plan.features?.length || 0} display features</p>
                  <div className="text-xs text-stone-600 line-clamp-2">
                    {plan.features?.slice(0, 3).join(' • ')}
                    {plan.features?.length > 3 && '...'}
                  </div>
                </div>

                {/* Stripe Status */}
                <div className="mb-4 p-2 bg-purple-50 rounded-lg">
                  <p className="text-xs font-medium text-purple-700 mb-1">💳 Stripe Status</p>
                  <div className="flex gap-2 text-xs">
                    <span className={`px-2 py-0.5 rounded ${plan.stripe_price_id_monthly ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                      Monthly: {plan.stripe_price_id_monthly ? '✓' : '—'}
                    </span>
                    <span className={`px-2 py-0.5 rounded ${plan.stripe_price_id_annual ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                      Annual: {plan.stripe_price_id_annual ? '✓' : '—'}
                    </span>
                  </div>
                </div>

                {/* Share Link */}
                <div className="mb-4 p-2 bg-amber-50 rounded-lg">
                  <p className="text-xs font-medium text-amber-700 mb-1">🔗 Share Link</p>
                  <div className="flex items-center gap-1">
                    <code className="text-xs bg-white px-2 py-1 rounded border flex-1 truncate text-stone-600">
                      {window.location.origin}/plan/{plan.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}
                    </code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/plan/${plan.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`); toast.success('Plan link copied!'); }}
                      className="px-2 py-1 bg-amber-100 hover:bg-amber-200 rounded text-amber-700 text-xs font-medium"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingPlan(plan)}
                    className="flex-1"
                  >
                    ✏️ Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleVisibility(plan)}
                    className={plan.is_visible ? 'text-orange-600' : 'text-green-600'}
                  >
                    {plan.is_visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTogglePopular(plan)}
                    className={plan.is_popular ? 'text-amber-600' : ''}
                  >
                    <Star className={`w-4 h-4 ${plan.is_popular ? 'fill-amber-500' : ''}`} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeletePlan(plan.plan_id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {plans.length === 0 && viewMode === 'cards' && (
          <Card className="p-8 text-center">
            <p className="text-stone-500 mb-4">No subscription plans found. Create your first plan!</p>
            <Button onClick={() => setShowAddPlan(true)} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" /> Create Plan
            </Button>
          </Card>
        )}
      </div>

      {/* Edit Modal */}
      {editingPlan && (
        <PlanEditor
          plan={editingPlan}
          onSave={handleSavePlan}
          onCancel={() => setEditingPlan(null)}
        />
      )}

      {/* Add Modal */}
      {showAddPlan && (
        <PlanEditor
          plan={null}
          onSave={handleCreatePlan}
          onCancel={() => setShowAddPlan(false)}
          isNew={true}
        />
      )}
    </div>
  );
};

export default AdminSubscriptionPlans;
